import { useEffect, useState } from 'react';
import { useT } from '@/i18n';
import { useAuthStore } from '@/store/auth';
import { useLoginNotificationsStore } from '@/store/loginNotifications';
import { NotificationDialog } from './NotificationDialog';

/**
 * Sign-in announcements, shown one after another straight after login or
 * register.
 *
 * The queue is filled by `loadLoginNotifications()` in the auth success
 * paths — this component never fetches, so it cannot re-request the endpoint
 * on navigation or resume.
 *
 * Dismissal is purely local: a login notice has no per-user row, and posting
 * its id to the read endpoint is refused with code 2172.
 */
export function LoginNotificationPopup() {
  const passwordWeak = useAuthStore((s) => s.passwordWeak);
  const tc = useT('common');
  const queue = useLoginNotificationsStore((s) => s.queue);
  const dismissFirst = useLoginNotificationsStore((s) => s.dismissFirst);
  const [open, setOpen] = useState(false);

  const item = queue[0] ?? null;

  // Open as soon as something lands, and again for each following notice
  // once the previous card has finished leaving.
  useEffect(() => {
    if (item) setOpen(true);
  }, [item]);

  // Nothing shows while the weak-password prompt is up. That dialog cannot be
  // dismissed, so an announcement stacked over it is either unreachable or a
  // second thing competing for a screen the user is meant to be blocked on.
  if (passwordWeak) return null;
  if (!item) return null;

  const title = item.notification?.title?.trim() ?? '';
  const content = item.notification?.content?.trim() ?? '';

  return (
    <NotificationDialog
      open={open}
      title={title}
      content={content}
      closeLabel={tc('aria_close')}
      onClose={() => setOpen(false)}
      // Drop it only after the exit animation, so the next announcement
      // animates in cleanly instead of swapping mid-fade.
      onClosed={dismissFirst}
    />
  );
}
